import { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Users,
  UserPlus,
  MoreHorizontal,
  Pencil,
  Trash2,
  Shield,
  ShieldCheck,
  Key,
  Search,
  Eye,
  EyeOff,
  AlertTriangle,
} from "lucide-react";
import { useAuth } from "@/contexts/auth-context";
import { User } from "@/types/auth";
import { DeleteDialog } from "@/components/ui/confirmation-dialog";

interface UsersPageProps {
  embedded?: boolean;
}

interface UserWithPassword extends User {
  password?: string;
}

export interface UsersPageRef {
  openAddDialog: () => void;
}

export const UsersPage = forwardRef<UsersPageRef, UsersPageProps>(({ embedded = true }, ref) => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  // Dialog states
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [changePasswordDialogOpen, setChangePasswordDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // Form states
  const [formData, setFormData] = useState({
    username: "",
    fullName: "",
    email: "",
    password: "",
    confirmPassword: "",
    role: "user" as "admin" | "user",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);

  // Expose openAddDialog to parent via ref
  useImperativeHandle(ref, () => ({
    openAddDialog: () => {
      resetForm();
      setAddDialogOpen(true);
    }
  }));

  const getApiUrl = () => import.meta.env.VITE_KV_API_URL || '';
  const getToken = () => import.meta.env.VITE_KV_TOKEN || '';

  // Fetch users
  const fetchUsers = async () => {
    setLoading(true);
    try {
      // گرتنی session token لە localStorage
      const sessionToken = localStorage.getItem('auth-token');

      // Get all users by listing storage keys
      const response = await fetch(`${getApiUrl()}/users`, {
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // Filter users
  const filteredUsers = users
    .filter(user => user.username !== 'azhersallah') // شاردنەوەی ئەدمینی سەرەکی
    .filter(user =>
      user.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.fullName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email.toLowerCase().includes(searchQuery.toLowerCase())
    );

  // Reset form
  const resetForm = () => {
    setFormData({
      username: "",
      fullName: "",
      email: "",
      password: "",
      confirmPassword: "",
      role: "user",
    });
    setFormError("");
    setShowPassword(false);
  };

  // Validate form
  const validateForm = (isEdit = false): boolean => {
    if (!formData.username.trim()) {
      setFormError("ناوی بەکارهێنەر پێویستە");
      return false;
    }
    if (!/^[a-zA-Z0-9_-]+$/.test(formData.username)) {
      setFormError("ناوی بەکارهێنەر تەنها پیت و ژمارە و _ و - دەگرێتەوە");
      return false;
    }
    if (!formData.fullName.trim()) {
      setFormError("ناوی تەواو پێویستە");
      return false;
    }
    if (!isEdit && !formData.password) {
      setFormError("وشەی نهێنی پێویستە");
      return false;
    }
    if (formData.password && formData.password.length < 6) {
      setFormError("وشەی نهێنی لانیکەم ٦ پیت دەبێت");
      return false;
    }
    if (formData.password && formData.password !== formData.confirmPassword) {
      setFormError("وشەی نهێنی یەک ناگرنەوە");
      return false;
    }
    return true;
  };

  // Add user
  const handleAddUser = async () => {
    if (!validateForm()) return;

    setSaving(true);
    setFormError("");

    try {
      const newUser: UserWithPassword = {
        id: crypto.randomUUID(),
        username: formData.username.toLowerCase(),
        fullName: formData.fullName,
        email: formData.email,
        role: formData.role,
        password: formData.password,
        createdAt: new Date(),
      };

      // گرتنی session token لە localStorage
      const sessionToken = localStorage.getItem('auth-token');

      const response = await fetch(`${getApiUrl()}/users/${newUser.username}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newUser),
      });

      if (response.ok) {
        await fetchUsers();
        setAddDialogOpen(false);
        resetForm();
      } else {
        const error = await response.json();
        setFormError(error.error || "هەڵە لە زیادکردنی بەکارهێنەر");
      }
    } catch (error) {
      setFormError("هەڵە لە پەیوەندی بە سێرڤەر");
    } finally {
      setSaving(false);
    }
  };

  // Edit user
  const handleEditUser = async () => {
    if (!selectedUser || !validateForm(true)) return;

    setSaving(true);
    setFormError("");

    try {
      const updatedUser: UserWithPassword = {
        ...selectedUser,
        fullName: formData.fullName,
        email: formData.email,
        role: formData.role,
      };

      if (formData.password) {
        updatedUser.password = formData.password;
      }

      // گرتنی session token لە localStorage
      const sessionToken = localStorage.getItem('auth-token');

      const response = await fetch(`${getApiUrl()}/users/${selectedUser.username}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updatedUser),
      });

      if (response.ok) {
        await fetchUsers();
        setEditDialogOpen(false);
        setSelectedUser(null);
        resetForm();
      } else {
        const error = await response.json();
        setFormError(error.error || "هەڵە لە نوێکردنەوەی بەکارهێنەر");
      }
    } catch (error) {
      setFormError("هەڵە لە پەیوەندی بە سێرڤەر");
    } finally {
      setSaving(false);
    }
  };

  // Delete user
  const handleDeleteUser = async () => {
    if (!selectedUser) return;

    setSaving(true);
    try {
      // گرتنی session token لە localStorage
      const sessionToken = localStorage.getItem('auth-token');

      // گرتنی CSRF token
      const csrfResponse = await fetch(`${getApiUrl()}/csrf-token`, {
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
        },
      });

      let csrfToken = '';
      if (csrfResponse.ok) {
        const csrfData = await csrfResponse.json();
        csrfToken = csrfData.csrfToken;
      }

      const response = await fetch(`${getApiUrl()}/users/${selectedUser.username}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'X-CSRF-Token': csrfToken,
        },
      });

      if (response.ok) {
        await fetchUsers();
        setDeleteDialogOpen(false);
        setSelectedUser(null);
      } else {
        const error = await response.json().catch(() => ({ error: 'هەڵە لە سڕینەوەی بەکارهێنەر' }));
        console.error('Delete failed:', error);
        alert(error.error || 'هەڵە لە سڕینەوەی بەکارهێنەر');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('هەڵە لە سڕینەوەی بەکارهێنەر');
    } finally {
      setSaving(false);
    }
  };

  // Change password
  const handleChangePassword = async () => {
    if (!selectedUser) return;
    if (!formData.password || formData.password.length < 6) {
      setFormError("وشەی نهێنی لانیکەم ٦ پیت دەبێت");
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setFormError("وشەی نهێنی یەک ناگرنەوە");
      return;
    }

    setSaving(true);
    setFormError("");

    try {
      const updatedUser = { ...selectedUser, password: formData.password };

      // گرتنی session token لە localStorage
      const sessionToken = localStorage.getItem('auth-token');

      const response = await fetch(`${getApiUrl()}/users/${selectedUser.username}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updatedUser),
      });

      if (response.ok) {
        setChangePasswordDialogOpen(false);
        setSelectedUser(null);
        resetForm();
      } else {
        setFormError("هەڵە لە گۆڕینی وشەی نهێنی");
      }
    } catch (error) {
      setFormError("هەڵە لە پەیوەندی بە سێرڤەر");
    } finally {
      setSaving(false);
    }
  };

  // Open edit dialog
  const openEditDialog = (user: User) => {
    setSelectedUser(user);
    setFormData({
      username: user.username,
      fullName: user.fullName,
      email: user.email,
      password: "",
      confirmPassword: "",
      role: user.role,
    });
    setEditDialogOpen(true);
  };

  // Render all dialogs (shared between embedded and overlay modes)
  const renderDialogs = () => (
    <>
      {/* Add User Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="w-[95vw] max-w-2xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>زیادکردنی بەکارهێنەری نوێ</DialogTitle>
            <DialogDescription>
              زانیاری بەکارهێنەری نوێ بنووسە
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={(e) => { e.preventDefault(); handleAddUser(); }} className="flex flex-col flex-1 overflow-hidden">
            <div className="flex-1 overflow-y-auto space-y-4 pb-4">
              {formError && (
                <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
                  {formError}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="username">ناوی بەکارهێنەر *</Label>
                  <Input
                    id="username"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value.toLowerCase() })}
                    placeholder="username"
                    required
                    className="h-10"
                    style={{ textTransform: 'lowercase' }}
                  />
                  <p className="text-xs text-muted-foreground">تەنها پیتی بچووک، ژمارە، _ و -</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="fullName">ناوی تەواو *</Label>
                  <Input
                    id="fullName"
                    value={formData.fullName}
                    onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                    placeholder="ناوی تەواو"
                    required
                    className="h-10"
                  />
                </div>
              </div>

              {/* ئیمەیڵ */}
              <div className="space-y-2">
                <Label htmlFor="email">ئیمەیڵ</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="email@example.com"
                  className="h-10"
                />
              </div>

              {/* وشەی نهێنی */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="password">وشەی نهێنی *</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder="لانیکەم ٦ پیت"
                      required
                      className="h-10"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute left-1 top-1/2 -translate-y-1/2 h-8 w-8"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">دووبارەکردنەوەی وشەی نهێنی *</Label>
                  <Input
                    id="confirmPassword"
                    type={showPassword ? "text" : "password"}
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    placeholder="دووبارە بنووسەرەوە"
                    required
                    className="h-10"
                  />
                </div>
              </div>

              {/* ڕۆڵ */}
              <div className="space-y-2">
                <Label>ڕۆڵ</Label>
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    type="button"
                    variant={formData.role === "user" ? "default" : "outline"}
                    className="h-10"
                    onClick={() => setFormData({ ...formData, role: "user" })}
                  >
                    <Shield className="h-4 w-4 ml-1" />
                    بەکارهێنەر
                  </Button>
                  <Button
                    type="button"
                    variant={formData.role === "admin" ? "default" : "outline"}
                    className="h-10"
                    onClick={() => setFormData({ ...formData, role: "admin" })}
                  >
                    <ShieldCheck className="h-4 w-4 ml-1" />
                    بەڕێوەبەر
                  </Button>
                </div>
              </div>
            </div>

            {/* دوگمەکان */}
            <div className="flex flex-row gap-2 pt-4 border-t bg-background sticky bottom-0">
              <Button type="button" variant="outline" onClick={() => setAddDialogOpen(false)} disabled={saving} className="flex-1">
                پاشگەزبوونەوە
              </Button>
              <Button type="submit" disabled={saving} className="flex-1">
                {saving ? "زیادکردن..." : "زیادکردن"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="w-[95vw] max-w-2xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>دەستکاریکردنی بەکارهێنەر</DialogTitle>
            <DialogDescription>
              زانیاری بەکارهێنەر بگۆڕە
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={(e) => { e.preventDefault(); handleEditUser(); }} className="flex flex-col flex-1 overflow-hidden">
            <div className="flex-1 overflow-y-auto space-y-4 pb-4">
              {formError && (
                <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
                  {formError}
                </div>
              )}

              {/* ناوی بەکارهێنەر و ناوی تەواو */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="edit-username">ناوی بەکارهێنەر</Label>
                  <Input
                    id="edit-username"
                    value={formData.username}
                    disabled
                    className="h-10 bg-muted"
                  />
                  <p className="text-xs text-muted-foreground">ناتوانرێت بگۆڕدرێت</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-fullName">ناوی تەواو *</Label>
                  <Input
                    id="edit-fullName"
                    value={formData.fullName}
                    onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                    placeholder="ناوی تەواو"
                    required
                    className="h-10"
                  />
                </div>
              </div>

              {/* ئیمەیڵ */}
              <div className="space-y-2">
                <Label htmlFor="edit-email">ئیمەیڵ</Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="email@example.com"
                  className="h-10"
                />
              </div>

              {/* ڕۆڵ */}
              <div className="space-y-2">
                <Label>ڕۆڵ</Label>
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    type="button"
                    variant={formData.role === "user" ? "default" : "outline"}
                    className="h-10"
                    onClick={() => setFormData({ ...formData, role: "user" })}
                    disabled={selectedUser?.username === currentUser?.username}
                  >
                    <Shield className="h-4 w-4 ml-1" />
                    بەکارهێنەر
                  </Button>
                  <Button
                    type="button"
                    variant={formData.role === "admin" ? "default" : "outline"}
                    className="h-10"
                    onClick={() => setFormData({ ...formData, role: "admin" })}
                    disabled={selectedUser?.username === currentUser?.username}
                  >
                    <ShieldCheck className="h-4 w-4 ml-1" />
                    بەڕێوەبەر
                  </Button>
                </div>
                {selectedUser?.username === currentUser?.username && (
                  <p className="text-xs text-muted-foreground">ناتوانیت ڕۆڵی خۆت بگۆڕیت</p>
                )}
              </div>
            </div>

            {/* دوگمەکان */}
            <div className="flex flex-row gap-2 pt-4 border-t bg-background sticky bottom-0">
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)} disabled={saving} className="flex-1">
                پاشگەزبوونەوە
              </Button>
              <Button type="submit" disabled={saving} className="flex-1">
                {saving ? "پاشەکەوتکردن..." : "پاشەکەوتکردن"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={changePasswordDialogOpen} onOpenChange={setChangePasswordDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>گۆڕینی وشەی نهێنی</DialogTitle>
            <DialogDescription>
              وشەی نهێنی نوێ بۆ {selectedUser?.fullName}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {formError && (
              <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
                {formError}
              </div>
            )}
            <div className="space-y-2">
              <Label>وشەی نهێنی نوێ *</Label>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="لانیکەم ٦ پیت"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute left-1 top-1/2 -translate-y-1/2 h-7 w-7"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>دووبارەکردنەوەی وشەی نهێنی *</Label>
              <Input
                type={showPassword ? "text" : "password"}
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setChangePasswordDialogOpen(false)}>
              پاشگەزبوونەوە
            </Button>
            <Button onClick={handleChangePassword} disabled={saving}>
              {saving ? "چاوەڕوان بە..." : "گۆڕین"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <DeleteDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        itemName={selectedUser?.fullName}
        onConfirm={handleDeleteUser}
      />
    </>
  );

  // Check if current user is admin
  const isAdmin = currentUser?.role === "admin";

  if (!isAdmin) {
    return (
      <Card className="w-full max-w-md mx-auto" dir="rtl">
        <CardHeader className="text-center">
          <div className="mx-auto h-12 w-12 rounded-full bg-destructive/10 flex items-center justify-center mb-2">
            <AlertTriangle className="h-6 w-6 text-destructive" />
          </div>
          <CardDescription className="text-base">
            تەنها بەڕێوەبەر دەتوانێت لیستی بەکارهێنەران ببینێت
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div className="space-y-4" dir="rtl">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="rounded-xl border bg-card p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="h-8 w-8 rounded-lg bg-blue-500/10 flex items-center justify-center">
              <Users className="h-4 w-4 text-blue-600" />
            </div>
          </div>
          <p className="text-2xl font-bold">{users.filter(u => u.username !== 'azhersallah').length}</p>
          <p className="text-xs text-muted-foreground">کۆی بەکارهێنەران</p>
        </div>
        <div className="rounded-xl border bg-card p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="h-8 w-8 rounded-lg bg-purple-500/10 flex items-center justify-center">
              <ShieldCheck className="h-4 w-4 text-purple-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-purple-600">{users.filter(u => u.role === 'admin' && u.username !== 'azhersallah').length}</p>
          <p className="text-xs text-muted-foreground">بەڕێوەبەر</p>
        </div>
        <div className="rounded-xl border bg-card p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="h-8 w-8 rounded-lg bg-green-500/10 flex items-center justify-center">
              <Shield className="h-4 w-4 text-green-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-green-600">{users.filter(u => u.role === 'user').length}</p>
          <p className="text-xs text-muted-foreground">بەکارهێنەر</p>
        </div>
        <div className="rounded-xl border bg-card p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="h-8 w-8 rounded-lg bg-amber-500/10 flex items-center justify-center">
              <UserPlus className="h-4 w-4 text-amber-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-amber-600">{users.filter(u => u.username !== 'azhersallah').length}</p>
          <p className="text-xs text-muted-foreground">چالاک</p>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
            <CardTitle className="flex items-center gap-2 text-base md:text-lg">
              <Users className="h-5 w-5" />
              لیستی بەکارهێنەران
            </CardTitle>
            <div className="relative flex-1 md:w-64">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="گەڕان..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pr-9"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="px-2 md:px-6">
          {loading ? (
            <div className="text-center py-12 text-muted-foreground">
              چاوەڕوان بە...
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              {searchQuery ? "هیچ بەکارهێنەرێک نەدۆزرایەوە" : "هیچ بەکارهێنەرێک نییە"}
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden md:block rounded-lg border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-right">ناوی بەکارهێنەر</TableHead>
                      <TableHead className="text-right">ناوی تەواو</TableHead>
                      <TableHead className="text-right">ئیمەیڵ</TableHead>
                      <TableHead className="text-right">ڕۆڵ</TableHead>
                      <TableHead className="text-right">دوایین چوونەژوورەوە</TableHead>
                      <TableHead className="w-12"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredUsers.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell className="font-medium">{user.username}</TableCell>
                        <TableCell>{user.fullName}</TableCell>
                        <TableCell dir="ltr" className="text-right">{user.email || "-"}</TableCell>
                        <TableCell>
                          <Badge variant={user.role === "admin" ? "default" : "secondary"}>
                            {user.role === "admin" ? (
                              <><ShieldCheck className="h-3 w-3 ml-1" />بەڕێوەبەر</>
                            ) : (
                              <><Shield className="h-3 w-3 ml-1" />بەکارهێنەر</>
                            )}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {user.lastLogin ? new Date(user.lastLogin).toLocaleDateString('ku') : "-"}
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                              <DropdownMenuItem onClick={() => openEditDialog(user)}>
                                <Pencil className="h-4 w-4 ml-2" />
                                دەستکاریکردن
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => {
                                setSelectedUser(user);
                                resetForm();
                                setChangePasswordDialogOpen(true);
                              }}>
                                <Key className="h-4 w-4 ml-2" />
                                گۆڕینی وشەی نهێنی
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                disabled={user.username === currentUser?.username}
                                onClick={() => {
                                  setSelectedUser(user);
                                  setDeleteDialogOpen(true);
                                }}
                                className="text-destructive focus:text-destructive"
                              >
                                <Trash2 className="h-4 w-4 ml-2" />
                                سڕینەوە
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Mobile Cards */}
              <div className="md:hidden space-y-3">
                {filteredUsers.map((user) => (
                  <Card key={user.id}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                            {user.role === "admin" ? (
                              <ShieldCheck className="h-5 w-5 text-primary" />
                            ) : (
                              <Shield className="h-5 w-5 text-muted-foreground" />
                            )}
                          </div>
                          <div>
                            <p className="font-semibold">{user.fullName}</p>
                            <p className="text-sm text-muted-foreground">@{user.username}</p>
                          </div>
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48">
                            <DropdownMenuItem onClick={() => openEditDialog(user)}>
                              <Pencil className="h-4 w-4 ml-2" />
                              دەستکاریکردن
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => {
                              setSelectedUser(user);
                              resetForm();
                              setChangePasswordDialogOpen(true);
                            }}>
                              <Key className="h-4 w-4 ml-2" />
                              گۆڕینی وشەی نهێنی
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              disabled={user.username === currentUser?.username}
                              onClick={() => {
                                setSelectedUser(user);
                                setDeleteDialogOpen(true);
                              }}
                              className="text-destructive focus:text-destructive"
                            >
                              <Trash2 className="h-4 w-4 ml-2" />
                              سڕینەوە
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                      <div className="mt-3 pt-3 border-t space-y-1 text-sm">
                        {user.email && (
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">ئیمەیڵ</span>
                            <span dir="ltr">{user.email}</span>
                          </div>
                        )}
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">ڕۆڵ</span>
                          <Badge variant={user.role === "admin" ? "default" : "secondary"} className="text-xs">
                            {user.role === "admin" ? "بەڕێوەبەر" : "بەکارهێنەر"}
                          </Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Dialogs */}
      {renderDialogs()}
    </div>
  );
});
